import { useState } from "react";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Menu, X } from "lucide-react";

export function SiteHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { label: "Football Home", href: "/", search: { cat: "home" } },
    { label: "World Cup 2026", href: "/", search: { cat: "world-cup" } },
    { label: "International", href: "/", search: { cat: "international" } },
    { label: "European Leagues", href: "/", search: { cat: "european" } },
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
    { label: "Terms", href: "/terms" },
    { label: "Privacy", href: "/privacy" },
  ];

  return (
    <header className="border-b border-border bg-background">
      {/* Top black bar */}
      <div className="bg-ink text-white">
        <div className="container-page flex h-11 items-center justify-between text-xs">
          <Link to="/" className="font-semibold tracking-wide hover:underline">WEBORBIS</Link>
          <div className="text-white/70 hidden md:block">weborbis.top</div>
        </div>
      </div>

      {/* Main header with logo and nav */}
      <div className="bg-brand text-brand-foreground">
        <div className="container-page">
          <div className="flex items-center justify-between py-4">
            <Link to="/" className="text-3xl font-extrabold tracking-tight">SPORT</Link>
            
            {/* Desktop navigation */}
            <nav className="hidden md:flex items-center gap-6">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  search={item.search}
                  className="text-sm font-medium hover:text-white transition-colors"
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 hover:bg-white/10 rounded-md transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-white/20 pt-4">
              <nav className="flex flex-col gap-3">
                {navItems.map((item) => (
                  <Link
                    key={item.label}
                    to={item.href}
                    search={item.search}
                    className="text-sm font-medium hover:text-white transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="mt-16 border-t border-border bg-ink text-white/80">
      <div className="container-page py-10 grid gap-8 md:grid-cols-4 text-sm">
        <div>
          <div className="text-white font-bold text-lg">WEBORBIS SPORT</div>
          <p className="mt-2 text-white/60">
            Independent international football coverage — match reports, tactical analysis and
            breaking news from the world's biggest tournaments.
          </p>
          <p className="mt-3 text-white/50 text-xs">weborbis.top</p>
        </div>
        <div>
          <div className="text-white font-semibold mb-2">Sections</div>
          <ul className="space-y-1">
            <li><Link to="/" className="hover:text-white">Football Home</Link></li>
            <li><Link to="/" search={{ cat: "world-cup" }} className="hover:text-white">World Cup 2026</Link></li>
            <li><Link to="/" search={{ cat: "international" }} className="hover:text-white">International</Link></li>
            <li><Link to="/" search={{ cat: "european" }} className="hover:text-white">European Leagues</Link></li>
          </ul>
        </div>
        <div>
            <div className="text-white font-semibold mb-2">About</div>
            <ul className="space-y-1">
              <li><Link to="/about" className="hover:text-white">About Us</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-semibold mb-2">Legal</div>
            <ul className="space-y-1">
              <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
              <li><Link to="/user-agreement" className="hover:text-white">User Agreement</Link></li>
            </ul>
          </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-page py-4 text-xs text-white/50 flex flex-wrap justify-between gap-2">
          <span>© {new Date().getFullYear()} Weborbis Sport. All rights reserved.</span>
          <span>Editorial content for informational purposes.</span>
        </div>
      </div>
    </footer>
  );
}

export function PageShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <main className="flex-1">{children}</main>
      <SiteFooter />
    </div>
  );
}
